<template lang="pug">
#diagnostico-socioambiental-v2
    section.diagnostico-socioambiental__resultado
        .diagnostico-socioambiental__resultado__header
            h1 {{ $t('mensagens.diagnosticoSocioambiental.titulo') }}
        .diagnostico-socionambiental__resultado__protocolo
            section.d-flex.flex-column.mb-3(v-if='protocolos.length > 0')
                h3 {{ $t('mensagens.diagnosticoSocioambiental.selecioneProtocoloParaAnalisar') }}
                el-select(
                    :disabled='protocolos.length === 1'
                    v-model='selectedProtocolo'
                )
                    el-option(
                        v-for='protocolo in protocolos'
                        :key='protocolo.codigo'
                        :label='protocolo.descricao'
                        :value='protocolo.codigo'
                    )
        .diagnostico-socioambiental__resultado__body
            section.diagnostico-socioambiental__resultado__busca
                p {{ $t('mensagens.diagnosticoSocioambiental.utilizeCamposAbaixoParaRealizarDiagnostico') }}
                .d-flex
                    el-input(
                        v-model='consulta'
                        prefix-icon='el-icon-search'
                        clearable
                        :placeholder="$t('mensagens.diagnosticoSocioambiental.pesquisaPlaceholder')"
                        @keyup.enter.native='adicionarConsultaDaBusca'
                    )
                    el-tooltip(:content="$t('mensagens.comuns.pesquisar')" placement='top')
                        el-button.ml-2(
                            type='primary'
                            icon='el-icon-plus'
                            :disabled='!selectedProtocolo || !consulta'
                            @click='adicionarConsultaDaBusca'
                        )
                el-upload.mt-3(
                    action='alert'
                    :auto-upload='false'
                    :show-file-list='false'
                    :on-change='adicionarArquivo'
                )
                    el-button(icon='el-icon-upload' round) {{ $t('mensagens.diagnosticoSocioambiental.pesquisaPorArquivo') }}
                el-button.mt-3(
                    type='text'
                    icon='el-icon-files'
                    @click='mostrarModalDiagnosticoSociambientalEmLote = true'
                ) {{ $t('mensagens.diagnosticoSocioambiental.diagnosticoEmLote') }}
            section.diagnostico-socioambiental__resultado__lista(v-if='consultasProcessadas.length')
                .d-flex.justify-content-between.align-items-center.mb-2
                    h3 {{ $t('mensagens.diagnosticoSocioambiental.consultasProcessadas') }}
                    el-button(
                        v-if='consultasProcessadas.length > 1'
                        type='text'
                        icon='el-icon-delete'
                        @click='removerTodasConsultas'
                    ) {{ $t('mensagens.diagnosticoSocioambiental.removerTodos') }}
                el-collapse.diagnostico-socioambiental__resultado__collapse(v-model='consultasAbertas')
                    ItemConsultaProcessada(
                        v-for='(item, index) in consultasProcessadas'
                        :key='item.idRequest'
                        v-loading='item.loading'
                        :item='item'
                        :index='index'
                        :aberto='consultasAbertas.includes(item.idRequest)'
                        :marcado-para-exportacao='idsMarcadosParaExportacao.has(item.idRequest)'
                        :mapa-leaflet='refMapa'
                        :codigo-protocolo='selectedProtocolo'
                        @remover='removerConsulta'
                        @zoom='aplicarZoomGeometria'
                        @selecionar-exportacao='alternarSelecaoExportacao'
                        @exportar='exportarConsulta'
                        @camada-aberta='mostrarAreasDiagnosticoNoMapa'
                        @proximidade-processada='aoProcessarProximidade'
                    )
    section.diagnostico-socioambiental__mapa.mapa.w-100.h-100
        Mapa(
            ref='mapa'
            :showStatesLayers='true'
            :mapLayersConfigs='layersMaps'
            :mapConfig='mapConfig'
            :height='"100%"'
            idMapa='mapaDiagnosticoSocioambientalV2'
            :atualizarMapa='!mantendoEnquadramentoDoMapa'
            :cluster='false'
            :menuMaior='true'
        )
    ModalSelecionarDadosCpfCnpj(
        :show='mostrarModalDadosCpfCnpj'
        :cpfCnpj='consultaParaModal'
        :dados='dadosModalCpfCnpj'
        tipo='DIAGNOSTICO'
        @voltar='mostrarModalDadosCpfCnpj = false'
        @retornarDadosSelecionados='adicionarConsultaSelecionada'
    )
    ModalDiagnosticoSociambientalEmLote(
        :show='mostrarModalDiagnosticoSociambientalEmLote'
        :protocolo='selectedProtocolo'
        @voltar='mostrarModalDiagnosticoSociambientalEmLote = false'
    )
</template>

<script>
import { ProtocolosService } from '@/services/ProtocolosService';
import DiagnosticoSocioambientalService from '@/services/DiagnosticoSocioambientalService';
import SigefService from '@/services/SigefService';
import CarService from '@/services/car/CarService';
import ModalSelecionarDadosCpfCnpj from '@/components/ModalSelecionarDadosCpfCnpj.vue';
import ModalDiagnosticoSociambientalEmLote from '@/components/diagnosticoSocioambiental/ModalDiagnosticoSociambientalEmLote.vue';
import ItemConsultaProcessada from '@/components/diagnosticoSocioambiental/v2/ItemConsultaProcessada.vue';
import Mapa from '@/components/MapaLeaflet.vue';
import geoJsonConverter from '@/utils/conversorGeojson';
import formatters from '@/utils/formatters';
import CarUtils from '@/utils/CarUtils';
import { getEstiloGeometria } from '@/utils/mapa/mapa.estilos';
import { retryOperation } from '@/utils/retry-operation';
import { connect, disconnect, subscribe } from '@/utils/websocket';
import { mapActions, mapGetters } from 'vuex';
import { uuid } from 'vue-uuid';
import { AmbientesEmpresas } from '@/constants/AmbienteLogado';
import { FeatureFlagConst } from '@/constants/FeatureFlag';
import { formatFlag } from '@/utils/featureFlags/featureFlag';

// Consultas que viram GEOMETRIA no backend: arquivo vetorial e parcela SIGEF (inclusive a escolhida no modal do INCRA).
const TIPOS_GEOMETRIA = ['ARQUIVO', 'SIGEF'];

// Mirror: DiagnosticoSocioambiental.vue:786 (v1) e DiagnosticosSocioambientais.vue:763 (PS) - o modal reconhece a
// busca por INCRA por este literal e só oferece a ação por parcela (ModalSelecionarDadosCpfCnpj.vue, v-if do botão de
// CPF/CNPJ). Passar o número do INCRA fazia o botão de CPF/CNPJ aparecer e consultar o INCRA como documento (GS-4058).
const MODAL_INCRA = 'INCRA';

/**
 * Diagnóstico Socioambiental v2 (GS-3925).
 *
 * ESCOPO: esta tela hoje atende SOMENTE O BRASIL — decisão do dono do produto (2026-09-24).
 * Não há suporte a outros países nesta versão, e nada aqui foi desenhado para ser agnóstico
 * de país. Antes de habilitar a rota `/v2/diagnostico-socioambiental` para AR/PY/US, revisar
 * pelo menos: a fonte de produtividade (hoje CONAB, que é brasileira), os tipos de documento
 * aceitos na busca (CAR, CAR estadual MT, SIGEF, INCRA — todos registros brasileiros) e as
 * camadas do protocolo. O gate atual está em `adicionarConsulta` (getter `ambienteBr`,
 * store.js:223-225) e cobre só a capacidade produtiva, NÃO a tela inteira.
 *
 * Régua de manutenção: a v2 é uma cópia da v1 (`../DiagnosticoSocioambiental.vue`) somada à
 * lista de múltiplas consultas vinda do Parceria Sustentável. Divergência de comportamento em
 * relação à v1 que não venha da lista é bug, não decisão de design.
 */
export default {
    name: 'DiagnosticoSocioambientalV2',
    components: {
        ItemConsultaProcessada,
        Mapa,
        ModalDiagnosticoSociambientalEmLote,
        ModalSelecionarDadosCpfCnpj
    },
    data() {
        return {
            protocolos: [],
            selectedProtocolo: null,
            consulta: '',
            consultasProcessadas: [],
            mostrarModalDadosCpfCnpj: false,
            consultaParaModal: '',
            dadosModalCpfCnpj: { carEstadoPaginado: [], listaSigef: [] },
            consultasAbertas: [],
            layersMaps: [],
            camadasAtivas: [],
            refMapa: null,
            // true só enquanto as layers de uma restrição entram no mapa (GS-4151): o <Mapa> não reenquadra sozinho
            mantendoEnquadramentoDoMapa: false,
            mostrarModalDiagnosticoSociambientalEmLote: false,
            exportandoPdf: false,
            mapConfig: {
                controls: { zoomControl: true, camadasControl: true, fullscreen: true },
                inspecionar: true,
                diagnostico: true
            }
        };
    },
    computed: {
        ...mapGetters(['ambienteBr', 'marcarExportarPDF']),
        // A marcação para exportação é a lista do store, a mesma do painel "Documentos para exportação"
        // (ExportarDocumento.vue): o store pode recusar (limite de 5, documento repetido, exportação em andamento) e o
        // painel pode remover itens, então uma flag local no item ficava dessincronizada.
        idsMarcadosParaExportacao() {
            return new Set((this.marcarExportarPDF || []).map(marcado => marcado.idLista));
        },
        usuarioLogado() {
            return this.$store?.getters?.usuarioLogado || {};
        },
        ambienteNorthAmerica() {
            return this.usuarioLogado && this.usuarioLogado.paisGrupo === AmbientesEmpresas.NORTE_AMERICA;
        }
    },
    watch: {
        selectedProtocolo(protocoloAtual, protocoloAnterior) {
            if (protocoloAnterior && protocoloAtual !== protocoloAnterior && this.consultasProcessadas.length) {
                this.removerTodasConsultas();
            }
        },
        // Mirror: DiagnosticoSocioambiental.vue:412-436 (v1, mascaraPesquisa) - só a máscara de
        // CAR (XX-NNNNNNN-32caracteres). O GS grava geometry_source_code SEMPRE com essa máscara
        // (confirmado no banco); sem ela, um CAR digitado sem hífen nunca acha a capacidade
        // produtiva (busca por igualdade exata, VegaGeometryRepository.java). CPF/CNPJ, SIGEF e
        // INCRA continuam sem máscara aqui - fora do escopo desta rodada.
        consulta(valor) {
            if (!valor) return;
            const semFormatacao = valor.replace(/[^a-zA-Z0-9]/g, '');
            if (semFormatacao.length === 41) {
                this.consulta = CarUtils.addMascaraCar(valor).toUpperCase();
            }
        }
    },
    async created() {
        this.protocolos = await ProtocolosService.buscarProtocolosPorUsuario();
        const protocoloPadrao = this.protocolos.find(protocolo => protocolo.protocoloPadrao);
        this.selectedProtocolo = protocoloPadrao ? protocoloPadrao.codigo : null;
        this.$bus.$on('mapa:camadas-ativas-diagnostico', this.atualizarCamadasAtivas);
    },
    mounted() {
        this.refMapa = this.$refs.mapa;
    },
    beforeDestroy() {
        this.$bus.$off('mapa:camadas-ativas-diagnostico', this.atualizarCamadasAtivas);
        this.clearMarcarExportar_PDF();
    },
    methods: {
        // GS-3925: a seção "Capacidade Produtiva" (e a chamada que a alimenta) só existe com a flag ligada no Unleash.
        // Método, não computed: o `$unleash` não é reativo (o cliente carrega as flags depois de montado), então um
        // computed guardaria o `false` do primeiro render. Flag inexistente ou Unleash fora do ar = desligada.
        capacidadeProdutivaHabilitada() {
            return Boolean(
                this.$unleash &&
                    this.$unleash.isEnabled(formatFlag(FeatureFlagConst.GS3925_CAPACIDADE_PRODUTIVA))
            );
        },
        ...mapActions([
            'setMarcarExportar_PDF',
            'removeMarcarExportar_PDF',
            'clearMarcarExportar_PDF'
        ]),
        atualizarCamadasAtivas(camadas) {
            this.camadasAtivas = camadas.map(camada => camada.layerName);
        },
        identificarTipoConsulta(documento) {
            const texto = (documento || '').trim();
            const normalizado = texto.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();

            if (normalizado.startsWith('MT') && normalizado.length <= 12) return 'CAR_ESTADUAL_MT';
            if (texto.length === 13) return 'INCRA';
            if (texto.length === 36) return 'SIGEF';
            if (texto.length === 43 || normalizado.length === 41) return 'CODIGOCAR';
            if (normalizado.length === 11 || normalizado.length === 14) return 'CPF_CNPJ';
            return null;
        },

        chaveUnicidade(tipo, documento) {
            return `${tipo}:${String(documento || '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase()}`;
        },

        atualizarConsulta(idRequest, dados) {
            this.consultasProcessadas = this.consultasProcessadas.map(item =>
                item.idRequest === idRequest ? { ...item, ...dados } : item
            );
        },

        async adicionarConsultaDaBusca() {
            const tipo = this.identificarTipoConsulta(this.consulta);
            if (!tipo) {
                this.$notify.error({
                    message: this.$t('mensagens.diagnosticoSocioambiental.erro.pesquisaInvalidaVazia')
                });
                return;
            }

            try {
                if (tipo === 'SIGEF') {
                    await this.adicionarParcelaSigef(this.consulta);
                } else if (tipo === 'INCRA') {
                    const { data: listaSigef } = await SigefService.consultarINCRA(this.consulta);
                    if (!listaSigef || !listaSigef.length) {
                        this.$notify.error({
                            message: this.$t('mensagens.diagnosticoSocioambiental.erro.parcelaNaoEncontradaParcelaRelacionada')
                        });
                        return;
                    }
                    this.abrirModalDadosCpfCnpj(MODAL_INCRA, { carEstadoPaginado: [], listaSigef });
                } else if (tipo === 'CPF_CNPJ') {
                    await this.buscarDadosCpfCnpj(this.consulta);
                } else {
                    await this.adicionarConsulta({ tipo, documento: this.consulta });
                }
                this.consulta = '';
            } catch (erro) {
                this.$notify.error({ message: this.$t('mensagens.diagnosticoSocioambiental.erro.ValorNaoEncontrado') });
            }
        },

        abrirModalDadosCpfCnpj(cpfCnpj, dados) {
            this.consultaParaModal = cpfCnpj;
            this.dadosModalCpfCnpj = dados;
            this.mostrarModalDadosCpfCnpj = true;
        },

        async buscarDadosCpfCnpj(cpfCnpj) {
            const documento = cpfCnpj.replace(/\D/g, '');
            // allSettled: um 404 de negócio numa das fontes (documento sem parcela SIGEF, por exemplo) não pode
            // descartar o que a outra achou.
            const [sigef, car] = await Promise.allSettled([
                SigefService.consultarCpfCnpj(documento),
                CarService.buscarCarPorCpfCnpjVinculado(documento)
            ]);
            const listaSigef = sigef.status === 'fulfilled' ? sigef.value.data : [];
            const cars = car.status === 'fulfilled' ? car.value.data : null;

            if ((listaSigef && listaSigef.length) || (cars && cars.existemDados)) {
                this.abrirModalDadosCpfCnpj(cpfCnpj, {
                    ...cars,
                    listaSigef: listaSigef || []
                });
                return;
            }

            await this.adicionarConsulta({ tipo: 'CPF_CNPJ', documento: cpfCnpj });
        },

        async adicionarConsultaSelecionada(dado) {
            this.mostrarModalDadosCpfCnpj = false;
            try {
                if (dado.codigo) {
                    await this.adicionarConsulta({ tipo: 'CODIGOCAR', documento: dado.codigo });
                    return;
                }
                // Parcela escolhida no modal (vinda do INCRA ou do CPF/CNPJ): mesmo caminho da busca direta pelo código,
                // então a duplicidade também é pela parcela (RN14).
                if (dado.codigoParcela) {
                    await this.adicionarParcelaSigef(dado.codigoParcela);
                    return;
                }
                await this.adicionarConsulta({ tipo: 'CPF_CNPJ', documento: dado });
            } catch (erro) {
                this.$notify.error({ message: this.$t('mensagens.diagnosticoSocioambiental.erro.ValorNaoEncontrado') });
            }
        },

        // Mirror: DiagnosticoSocioambiental.vue:655-672 e :766-771 (v1). O endpoint devolve a geometria da parcela
        // como TEXTO JSON, e a consulta espera uma LISTA serializada uma vez ('[{"type":"MultiPolygon",...}]').
        // Sem o JSON.parse ia '"{...}"' e o backend respondia 400 (GS-4057).
        async adicionarParcelaSigef(codigoParcela) {
            const { data } = await SigefService.buscarGeometriaParcela(codigoParcela);
            if (!data) {
                this.$notify.error({ message: this.$t('mensagens.diagnosticoSocioambiental.erro.parcelaNaoEncontrada') });
                return;
            }
            const geometria = typeof data === 'string' ? JSON.parse(data) : data;
            await this.adicionarConsulta({
                tipo: 'SIGEF',
                documento: codigoParcela,
                arquivo: JSON.stringify([geometria]),
                geometriaImovel: geometria
            });
        },

        adicionarArquivo(file) {
            const reader = new FileReader();
            const extensao = file.name.split('.').pop().toLowerCase();
            reader.onload = async event => {
                try {
                    const geoJson = await geoJsonConverter.parse(extensao, event.target.result, file.raw);
                    const geometrias = geoJson.features.map(feature => feature.geometry);
                    await this.adicionarConsulta({
                        tipo: 'ARQUIVO',
                        documento: file.name,
                        arquivo: this.prepararArquivoParaConsulta(geometrias),
                        geometriaImovel: geometrias
                    });
                } catch (erro) {
                    this.$notify.error({ message: erro });
                }
            };
            reader.readAsText(file.raw);
        },

        // Mirror: DiagnosticoSocioambiental.vue:1373-1382 (v1) - várias features Polygon
        // de um mesmo arquivo são unidas num MultiPolygon antes de enviar ao backend.
        prepararArquivoParaConsulta(geometries) {
            return JSON.stringify(
                geometries?.length > 1 && geometries.every(g => g?.type === 'Polygon')
                    ? [{ type: 'MultiPolygon', coordinates: geometries.map(g => g.coordinates) }]
                    : geometries
            );
        },

        async adicionarConsulta({ tipo, documento, arquivo = null, geometriaImovel = null }) {
            // RN14: arquivo vetorial é repetido quando a GEOMETRIA é a mesma, não o nome do arquivo.
            const chaveUnicidade = tipo === 'ARQUIVO' ? `ARQUIVO:${arquivo}` : this.chaveUnicidade(tipo, documento);
            if (this.consultasProcessadas.some(item => item.chaveUnicidade === chaveUnicidade)) {
                this.$notify.warning({
                    message: this.$t('mensagens.diagnosticoSocioambiental.consultaJaInserida')
                });
                return;
            }

            const idRequest = uuid.v1();
            const item = {
                idRequest,
                tipo,
                documento,
                chaveUnicidade,
                arquivo,
                codigoProtocolo: this.selectedProtocolo,
                loading: true,
                erro: false,
                dados: null,
                informacoesGerais: null,
                idDiagnosticoSocioambiental: null,
                protocoloUtilizadoFormatado: null,
                diagnosticoProximidade: null,
                produtividade: null,
                exibirCapacidadeProdutiva: false
            };
            this.consultasProcessadas = [...this.consultasProcessadas, item];

            const ehGeometria = TIPOS_GEOMETRIA.includes(tipo);
            const tipoConsulta = ehGeometria ? 'GEOMETRIA' : tipo;
            const documentoConsulta = ehGeometria ? 'geometria' : documento;
            try {
                const resposta = await DiagnosticoSocioambientalService.consultar(
                    tipoConsulta,
                    documentoConsulta,
                    true,
                    false,
                    arquivo,
                    this.selectedProtocolo
                );

                const imovel = this.extrairImovel(resposta.data);

                if (!imovel) {
                    this.atualizarConsulta(idRequest, { loading: false, erro: true });
                    return;
                }

                if (ehGeometria && geometriaImovel && !imovel.imovel) {
                    imovel.imovel = { geometria: geometriaImovel };
                }

                // informacoes_gerais, idDiagnosticoSocioambiental e protocoloDescricao são campos da RAIZ da resposta
                // (DiagnosticoSocioambiental.vue:805-812 na v1), não de dataCAR[i].
                this.atualizarConsulta(idRequest, {
                    dados: imovel,
                    informacoesGerais: resposta.data.informacoes_gerais || null,
                    idDiagnosticoSocioambiental: resposta.data.idDiagnosticoSocioambiental,
                    protocoloUtilizadoFormatado: resposta.data.protocoloDescricao,
                    loading: false
                });
                this.adicionarTerritorioAoMapa(idRequest, imovel);

                // Capacidade produtiva (RN26): só na consulta por CAR, e só no Brasil (getter ambienteBr,
                // store.js:223-225). A seção aparece mesmo sem dado na tabela de inteligência, com "--" (RN34): 204,
                // falha ou imóvel desconhecido pelo GS viram produtividade nula. Com a flag desligada nada disso roda:
                // sem chamada e sem seção.
                if (tipo === 'CODIGOCAR' && this.ambienteBr && this.capacidadeProdutivaHabilitada()) {
                    let produtividade = null;
                    try {
                        const respostaProdutividade = await DiagnosticoSocioambientalService.buscarProdutividadeCar(documento);
                        // 204 chega como '' e a prop do bloco espera Object
                        produtividade = respostaProdutividade.data || null;
                    } catch (erroProdutividade) {
                        produtividade = null;
                    }
                    this.atualizarConsulta(idRequest, { produtividade, exibirCapacidadeProdutiva: true });
                }
            } catch (erro) {
                this.atualizarConsulta(idRequest, { loading: false, erro: true });
            }
        },

        // Mirror: DiagnosticoSocioambiental.vue:814-830 (v1). Uma consulta = um item na lista (RN06): a busca por
        // documento é apresentada num item só, como a v1 já fazia com dataCAR[0].
        extrairImovel(dadosResposta) {
            const dataCAR = dadosResposta?.dataCAR;
            if (!Array.isArray(dataCAR) || dataCAR.length === 0) return null;

            if (dataCAR[0] && dataCAR[0].diagnosticos) {
                return { ...dataCAR[0], diagnosticos: this.ordenarDiagnosticoPorCamada(dataCAR[0].diagnosticos) };
            }

            // array plano de diagnosticos, sem wrap por imóvel (geometria/arquivo sem CAR cadastrado)
            return { diagnosticos: this.ordenarDiagnosticoPorCamada(dataCAR) };
        },

        // Rótulo que o cabeçalho da restrição exibe (index.vue, definirNomeCamada). A lista precisa ser ordenada por
        // ele, e não por camada.nome do banco, que diverge do texto da tela (ex.: QUILOMBOLAS_BA é "Quilombolas BA"
        // no banco e "Comunidades Quilombolas BA" na tela) (GS-4152).
        rotuloCamada(diagnostico) {
            return String(this.$t(`mensagens.camadas.${diagnostico.camada.codigo}`));
        },

        // Mirror: DiagnosticosSocioambientais.vue:1288-1302 (PS) - ordem alfabética pelo nome da camada como o
        // usuário lê, com localeCompare: "Á" fica junto do "A" (GS-4152). A v1 do Lyra ordena por código; o PS é a
        // régua visual pedida. Mantém o resto igual à v1 (DiagnosticoSocioambiental.vue:600-627).
        ordenarDiagnosticoPorCamada(diagnosticos) {
            diagnosticos = diagnosticos.map(d => ({
                ...d,
                camada: { ...d.camada, opacidade: 100 },
                nivelAlerta: d.nivelAlerta,
                status: d.status
            }));

            diagnosticos.forEach(diagnostico => {
                if (diagnostico.camada.nome === 'Áreas embargadas') {
                    diagnostico.camada.nome = 'Áreas embargadas (IBAMA)';
                } else if (diagnostico.camada.nome === 'Monitoramento Desmatamento') {
                    diagnostico.camada.nome = 'Desmatamento Lyra';
                }

                if (this.ambienteNorthAmerica) {
                    diagnostico.resultados = diagnostico.resultados.filter(
                        resultado => Number(resultado?.properties?.areaHa) > 1
                    );
                }
            });

            return this.ordenarPorRotuloCamada(diagnosticos);
        },

        ordenarPorRotuloCamada(diagnosticos) {
            return diagnosticos
                .map(diagnostico => ({ diagnostico, rotulo: this.rotuloCamada(diagnostico) }))
                .sort((a, b) => a.rotulo.localeCompare(b.rotulo, 'pt-BR'))
                .map(({ diagnostico }) => diagnostico);
        },

        // A lista de "Analisar proximidades" segue a mesma ordem da lista de restrições (RN42 / CA30, GS-4194).
        registrarProximidade(idRequest, diagnosticoProximidade) {
            this.atualizarConsulta(idRequest, {
                diagnosticoProximidade: {
                    ...diagnosticoProximidade,
                    diagnosticos: this.ordenarPorRotuloCamada(diagnosticoProximidade.diagnosticos || [])
                }
            });
        },

        // GS-4195: além de registrar o diagnóstico, desenha o raio da análise no mapa (ver mostrarProximidadeNoMapa).
        aoProcessarProximidade(idRequest, diagnosticoProximidade) {
            this.registrarProximidade(idRequest, diagnosticoProximidade);
            return this.mostrarProximidadeNoMapa(idRequest, diagnosticoProximidade.geometria);
        },

        // GS-4195 (RN43 / CA31): o raio da análise (geometria de buffer que o backend devolve) é desenhado no mapa e o
        // zoom passa a mostrá-lo por inteiro, como na v1 (DiagnosticoSocioambiental.vue:972-995, layer 'Território' do
        // buffer). O enquadramento é um só: o <Mapa> não reenquadra sozinho enquanto a layer entra (ver GS-4151).
        async mostrarProximidadeNoMapa(idRequest, geometrias) {
            const nome = `Proximidade-${idRequest}`;
            const camadasDaProximidade = geometrias?.length
                ? [{ idRequest, style: getEstiloGeometria('area'), name: nome, geoJson: geometrias }]
                : [];

            this.mantendoEnquadramentoDoMapa = true;
            this.layersMaps = this.layersMaps.filter(camada => camada.name !== nome).concat(camadasDaProximidade);
            await this.$nextTick();
            this.mantendoEnquadramentoDoMapa = false;

            if (camadasDaProximidade.length && this.refMapa) this.refMapa.enquadrarGeoJsons(geometrias);
        },

        removerConsulta(idRequest) {
            if (this.idsMarcadosParaExportacao.has(idRequest)) {
                this.removeMarcarExportar_PDF(idRequest);
            }
            this.consultasProcessadas = this.consultasProcessadas.filter(item => item.idRequest !== idRequest);
            this.consultasAbertas = this.consultasAbertas.filter(id => id !== idRequest);
            this.layersMaps = this.layersMaps.filter(camada => camada.idRequest !== idRequest);
        },

        // A mutation de remoção filtra por idLista (store.js:436-443): remover passando o objeto não tirava nada
        // da lista, e o item desmarcado continuava indo no lote.
        alternarSelecaoExportacao(item) {
            if (this.idsMarcadosParaExportacao.has(item.idRequest)) {
                this.removeMarcarExportar_PDF(item.idRequest);
            } else {
                this.setMarcarExportar_PDF(this.montarDadosPdf(item));
            }
        },

        montarDadosPdf(item, buffer = null) {
            let documento = item.documento;
            if (item.tipo === 'CODIGOCAR' || item.tipo === 'CAR_ESTADUAL_MT') {
                documento = String(item.documento).toUpperCase();
            } else if (TIPOS_GEOMETRIA.includes(item.tipo)) {
                // item.arquivo já é a LISTA serializada ('[{...}]'), como a v1 manda (DiagnosticoSocioambiental.vue:1147,
                // 1296). Embrulhar de novo gerava '[[{...}]]': GeometryUtils.parseGeoJson só tira um par de colchetes, a
                // página do PDF quebrava e o lote inteiro falhava no timeout do wkhtmltopdf (GS-4059).
                documento = item.arquivo;
            } else if (item.tipo === 'CPF_CNPJ') {
                documento = formatters.cpfCnpjFormatToNumeral(item.documento);
            }
            return {
                idLista: item.idRequest,
                tipoDocumento: TIPOS_GEOMETRIA.includes(item.tipo) ? 'GEOMETRIA' : item.tipo,
                documento,
                camadas: this.camadasAtivas,
                buffer,
                // só o painel de exportação lê `nome` (o TipoPdfDTO do back não tem o campo): sai como na lista (RN07)
                nome: item.tipo === 'CPF_CNPJ' ? formatters.formatarDocumentoInput(String(item.documento)) : item.documento,
                nomeArquivo: item.tipo === 'ARQUIVO' ? item.documento : null,
                protocolo: item.codigoProtocolo,
                // o painel de exportação mostra "(Protocolo: ...)" a partir deste campo (ExportarDocumento.vue:18; v1 :1255)
                protocoloFormatado: item.protocoloUtilizadoFormatado
            };
        },

        // Mirror: DiagnosticoSocioambiental.vue:1102-1201 (v1, exportarPdfDiagnosticoSocio).
        async exportarDados(dados, idsLoading) {
            // Todas as exportações assinam o mesmo tópico, sem id de correlação: uma por vez nesta tela, senão uma
            // receberia o PDF da outra.
            if (this.exportandoPdf) {
                this.$notify.warning({ message: this.$t('mensagens.sistema.exportacaoEmProcessamentoAguarde') });
                return;
            }

            try {
                await connect();
            } catch (erro) {
                this.$notify.error({ message: this.$t('mensagens.diagnosticoSocioambiental.erro.naoFoiPossivelConectarApi') });
                return;
            }

            const finalizar = () => {
                this.exportandoPdf = false;
                idsLoading.forEach(id => this.atualizarConsulta(id, { loadingDownloadPdf: false }));
                disconnect();
            };
            this.exportandoPdf = true;
            idsLoading.forEach(id => this.atualizarConsulta(id, { loadingDownloadPdf: true }));

            // Resposta vazia é o backend avisando que o PDF falhou (DiagnosticoSocioAmbientalService.gerarPdfs).
            subscribe(`${this.usuarioLogado.idKeyCloack}/exportar/pdf/diagnostico/socio`, response => {
                if (response) {
                    const link = document.createElement('a');
                    link.href = `data:application/pdf;base64,${response}`;
                    link.download = this.$t('mensagens.diagnosticoSocioambiental.pdfTitulo');
                    link.click();
                } else {
                    this.$notify.error({ message: this.$t('mensagens.diagnosticoSocioambiental.erro.naoFoiPossivelExportarPdf') });
                }
                finalizar();
            });

            try {
                // retryOperation chama operation(params) sem espalhar (retry-operation.js:4-6) e exportarPdf já embrulha
                // em { dados }: passar a lista direto. `[dados]` virava { dados: [[...]] } e o backend respondia 400
                // (GS-4060).
                await retryOperation(DiagnosticoSocioambientalService.exportarPdf, dados, 5000, 10);
            } catch (erro) {
                this.$notify.error({ message: this.$t('mensagens.diagnosticoSocioambiental.erro.naoFoiPossivelExportarPdf') });
                finalizar();
            }
        },

        exportarConsulta(item) {
            return this.exportarDados([this.montarDadosPdf(item)], [item.idRequest]);
        },

        removerTodasConsultas() {
            this.consultasProcessadas = [];
            this.consultasAbertas = [];
            this.layersMaps = [];
            this.clearMarcarExportar_PDF();
        },

        adicionarTerritorioAoMapa(idRequest, dados) {
            const geometria = dados?.imovel?.geometria || dados?.geometria;
            if (!geometria) return;
            this.layersMaps = [...this.layersMaps, {
                idRequest,
                style: getEstiloGeometria('area'),
                name: `Territorio-${idRequest}`,
                geoJson: geometria
            }];
        },

        // Geometria do imóvel (ou do arquivo/parcela) do item: a mesma que vira a layer 'Territorio-' do mapa.
        geometriaDoItem(item) {
            return item.dados?.imovel?.geometria || item.dados?.geometria;
        },

        aplicarZoomGeometria(item) {
            const geometria = this.geometriaDoItem(item);
            if (geometria && this.refMapa) this.refMapa.aplicarZoomGeoJson(geometria);
        },

        // GS-4151: abrir a restrição enquadrava TODAS as consultas da lista (o <Mapa> reenquadra todas as layers a cada
        // troca de mapLayersConfigs, e o index.vue também reenquadrava ao abrir). Com duas consultas distantes o zoom ia
        // para 7. Agora o <Mapa> não reenquadra sozinho (mantendoEnquadramentoDoMapa) e o enquadramento é um só, do
        // item: imóvel + geometrias da restrição. Dois fitBounds seguidos não servem: o Leaflet descarta o segundo
        // enquanto o primeiro ainda anima o zoom.
        async mostrarAreasDiagnosticoNoMapa(_, indexConsulta, indexRestricao) {
            if (indexRestricao === '' || indexRestricao === null) return;

            // ItemConsultaProcessada.vue usa indexCollapse = index para o card principal e
            // index + 1000 para a instância de proximidades do mesmo card (mirror do par
            // 0/1 fixo da v1, adaptado pra N cards). Mirror: DiagnosticoSocioambiental.vue:1008-1019.
            const ehProximidade = indexConsulta >= 1000;
            const item = this.consultasProcessadas[ehProximidade ? indexConsulta - 1000 : indexConsulta];
            if (!item) return;

            const diagnostico = ehProximidade
                ? item.diagnosticoProximidade?.diagnosticos?.[indexRestricao]
                : item.dados?.diagnosticos?.[indexRestricao];
            const resultados = diagnostico?.resultados || [];
            // group precisa bater com o que index.vue usa em atualizarOpacidade
            // (`grupo-restricoes-${indexCollapse}-${indexCollapseRow}`, index.vue:711-712) - sem
            // isso o slider de opacidade não encontra nenhuma layer pra alterar (bug confirmado:
            // MapaLeaflet.vue:960-961 só copia `layerConfig.group` quando ele existe).
            const group = `grupo-restricoes-${indexConsulta}-${indexRestricao}`;
            const camadas = resultados
                .filter(resultado => resultado.geometria || resultado.geometry)
                .map((resultado, index) => ({
                    idRequest: item.idRequest,
                    group,
                    name: `AreaDiagnostico-${item.idRequest}-${ehProximidade ? 'prox' : 'car'}-${indexRestricao}-${index}`,
                    style: { color: 'red', weight: 1, fill: true, opacity: 0.5, fillOpacity: 0.5 },
                    geoJson: resultado.geometria || resultado.geometry
                }));
            this.mantendoEnquadramentoDoMapa = true;
            this.layersMaps = this.layersMaps
                .filter(
                    camada =>
                        camada.idRequest !== item.idRequest ||
                        camada.name.startsWith('Territorio-') ||
                        camada.name.startsWith('Proximidade-')
                )
                .concat(camadas);

            // o watcher de mapLayersConfigs do <Mapa> roda neste mesmo ciclo de render, ainda com a prop desligada
            await this.$nextTick();
            this.mantendoEnquadramentoDoMapa = false;
            if (this.refMapa) {
                this.refMapa.enquadrarGeoJsons([this.geometriaDoItem(item), ...camadas.map(camada => camada.geoJson)]);
            }
        }
    }
};
</script>

<style lang="scss">
// Mirror do padrão de lista/scroll da tela do PS (DiagnosticosSocioambientais.vue): busca fica
// fixa no topo, só a lista de consultas rola. Antes, `.resultado` (overflow-y:auto) e
// `.resultado__body` (overflow-y:auto, height:calc(100% - 79px) herdado da v1) criavam DOIS
// scrolls empilhados - a conta de 79px supunha que o seletor de protocolo some quando há
// resultado (regra da v1), o que a v2 nunca faz.
#diagnostico-socioambiental-v2 {
    padding: 20px;
    width: 100%;
    height: 100%;
    display: flex;

    .diagnostico-socioambiental__resultado {
        background-color: var(--white);
        border-radius: 5px 0 0 5px;
        padding: 20px;
        width: 65%;
        display: flex;
        flex-direction: column;
        overflow: hidden;

        .diagnostico-socioambiental__resultado__header {
            height: auto;
            margin-bottom: 32px;
            display: flex;
            align-items: center;
            flex-shrink: 0;

            h1 {
                font: 500 18px Roboto, sans-serif;
            }
        }

        .diagnostico-socionambiental__resultado__protocolo {
            flex-shrink: 0;
        }

        .diagnostico-socioambiental__resultado__body {
            flex: 1;
            min-height: 0;
            display: flex;
            flex-direction: column;

            p {
                font: 500 14px Roboto, sans-serif;
            }

            .diagnostico-socioambiental__resultado__busca {
                flex-shrink: 0;
            }

            .diagnostico-socioambiental__resultado__lista {
                @include scroll-bar();
                flex: 1;
                min-height: 0;
                overflow-y: auto;
                padding-right: 8px;

                h3 {
                    font-size: 16px;
                    font-weight: 500;
                    margin: 16px 0 8px;
                }

                .diagnostico-socioambiental__resultado__collapse {
                    display: flex;
                    flex-direction: column;
                }
            }
        }
    }

    .diagnostico-socioambiental__mapa {
        width: 55%;
    }
}
</style>
